import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import type * as express from "express";
import { OperatorFunction, of } from "rxjs";
import {
    catchError,
    filter,
    map,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from "rxjs/operators";

import {
    MessageOption,
    MessageType,
    NotificationsService,
} from "@hermes/aphrodite/layout";
import {
    Context,
    LOCALE,
    REQUEST,
    RESPONSE,
    StorageService,
    WINDOW,
} from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { Logger } from "@hermes/logger";
import {
    ANGULAR_SPA_ACTIVATION_ON_PERSO_GENERIC_CONFIGURATOR,
    FeatureFlagFacade,
    SEO_COMBINED_FACETS_ACTIVATION_ON_GRID,
} from "@hermes/states/flipper";
import { PRODUCT_LEATHER_CATEGORY } from "@hermes/states/personalization-page";
import { generateLocalizedHref } from "@hermes/utils/helpers";
import { redirectResponse } from "@hermes/utils/services/response";
import { initFocusOnElement } from "@hermes/utils-generic/helpers";
import { FocusService } from "@hermes/utils-generic/services/focus";
import { RouterService } from "@hermes/utils-generic/services/router";
import { UrlUtils } from "@hermes/utils-generic/services/user-interface";
import { LOGGER } from "@hermes/web-logger";

import {
    changeDisplayMode,
    clearFilters,
    clearPriceFilter,
    closeDialog,
    fetchCategoryMetas,
    fetchCategoryMetasFailure,
    fetchCategoryMetasSuccess,
    fetchGridDatasFailure,
    fetchGridDatasSuccess,
    initGridPage,
    loadMoreProducts,
    loadMoreProductsSuccess,
    navigateToGridPage,
    scrollToPreviousProduct,
    selectGridItem,
    sendPageViewEvent,
    updateGridFilter,
    updateGridFilterPrice,
    updateGridSort,
    updateSeoPaginationHeadLinks,
    updateUrl,
} from "../actions/grid.actions";
import { GridFacade } from "../facades/grid.facade";
import { getPageSizeValue } from "../helpers/criterias-helper/criterias.helper";
import {
    gridItemIdFromSku,
    isFromSameFhLocation,
    retrieveGridDataWithoutNoDisplayedVideoAssets,
    skuToGridProductId,
} from "../helpers/grid-product.helper";
import { getPageViewType } from "../helpers/gtm.helper";
import {
    GRID_PAGE,
    GridData,
    GridType,
    PRODUCT_PAGE,
} from "../model/grid.model";
import * as fromGrid from "../reducers/grid.reducer";
import {
    selectBackToGridProperties,
    selectCategory,
    selectCriterias,
    selectFacet,
    selectGridState,
    selectHasSelectedFilters,
    selectIsFromPreviousGrid,
    selectPage,
    selectPreviousPage,
    selectUpdateUrlFacet,
    selectUrl,
} from "../selectors/grid.selectors";
import { CategoryService } from "../services/category/category.service";
import { GridAnalyticsService } from "../services/grid-analytics-service/grid-analytics.service";
import { GridPageHeadService } from "../services/grid-page-head/grid-page-head.service";
import { MerchandisingService } from "../services/merchandising/merchandising.service";
import { UrlManagerService } from "../services/url-manager-service/url-manager.service";

@Injectable()
export class GridEffects {
    public fetchInitGridDatas$ = createEffect(() =>
        // Supports pageNumber criteria.
        this.actions$.pipe(
            ofType(initGridPage),
            withLatestFrom(
                this.featureFlagFacade.isActivated(
                    SEO_COMBINED_FACETS_ACTIVATION_ON_GRID,
                ),
                this.store.select(selectCriterias),
                this.store.select(selectFacet),
                this.store.select(selectPage),
                this.store.select(selectUrl),
                this.store.select(selectCategory),
            ),
            switchMap(
                ([
                    action,
                    isCombinedFacetActivated,
                    criterias,
                    facet,
                    page,
                    url,
                    category,
                ]) => {
                    const { viewSize } = getPageSizeValue();

                    // compute pagination for fetching products
                    const offset = (criterias.pageNumber - 1) * viewSize;

                    // If change of grid page (novariation to color) -> load only one page.
                    // If previousPage = "product-page" -> back to grid from product page, pageLoaded taken from criterias
                    // but only if coming back to same grid page.
                    // FH can load 120 products maxi at each time, i.e 3 pages.
                    const pagesToLoad =
                        page.previousPage === GRID_PAGE ||
                        (page.previousPage === PRODUCT_PAGE &&
                            category?.pimCode !== page.previousCategoryPimCode)
                            ? 1
                            : Math.min(criterias.pagesLoaded, 3);
                    const pageSize = pagesToLoad * viewSize;

                    return this.merchandisingService
                        .fetchGridDataFromCriterias(
                            { ...criterias, pageSize, offset },
                            isCombinedFacetActivated,
                            facet,
                        )
                        .pipe(
                            map((gridData: GridData) => {
                                // If seo navigation (?page=123) and no product to display -> back to page 1 with 301.
                                if (
                                    action.pageNumber > 1 &&
                                    gridData.products.length === 0
                                ) {
                                    return navigateToGridPage();
                                }
                                // save grid datas
                                return fetchGridDatasSuccess({
                                    ...retrieveGridDataWithoutNoDisplayedVideoAssets(
                                        gridData,
                                    ),
                                    isFromPreviousGrid: isFromSameFhLocation(
                                        url,
                                        gridData.url,
                                    ),
                                });
                            }),
                            catchError((error) =>
                                of(fetchGridDatasFailure({ error })),
                            ),
                        );
                },
            ),
        ),
    );

    public reinitializePagesLoaded$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(initGridPage),
                withLatestFrom(
                    this.store.select(selectPage),
                    this.store.select(selectCategory),
                ),
                map(([_action, page, category]) => {
                    // if grid -> grid, page.pagesLoaded needs to be reinitialize
                    // if grid A -> product page -> grid B -> grid, page.pagesLoaded needs to be reinitialize
                    // Otherwise, we set previous page to grid-page.
                    if (
                        page.previousPage === GRID_PAGE ||
                        (page.previousPage === PRODUCT_PAGE &&
                            category?.pimCode !== page.previousCategoryPimCode)
                    ) {
                        this.gridFacade.reinitializePagesLoaded();
                    } else {
                        this.gridFacade.changePreviousPage(GRID_PAGE);
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * No support for pageNumber ->
     * if page browsed and filter applied, we should return to page 1 with filter applied.
     */
    public fetchGridDatas$ = createEffect(() => {
        const ofTypeFetchGridDatas = ofType(
            updateGridFilter,
            updateGridFilterPrice,
            updateGridSort,
            clearFilters,
            clearPriceFilter,
        );
        return this.gridEffect(
            this.actions$,
            this.store,
            this.featureFlagFacade,
            ofTypeFetchGridDatas,
            true,
        );
    });

    public fetchGridDatasSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fetchGridDatasSuccess),
                withLatestFrom(this.store.select(selectHasSelectedFilters)),
                tap(([action, hasSelectedFilters]) => {
                    // if there is a redirection url specified, go to this url and don't display grid
                    if (action.redirectionUrl) {
                        const redirectionUrl = new URL(action.redirectionUrl);
                        this.urlUtils.redirectToExternalUrl(
                            redirectionUrl.href,
                        );
                    }

                    // If there is no result on a filtered grid, show a notification
                    if (
                        !(action.products && action.products.length > 0) &&
                        hasSelectedFilters
                    ) {
                        const noResultMessage: MessageOption = {
                            type: MessageType.Info,
                            message: {
                                title: $localize`:@@hermes-global-translations.oops:Oops!`,
                                description: $localize`:@@hermes-global-translations.product_grid_msg_no_results_notification_description:Sorry, we currently do not have any item that match your search.`,
                            },
                            isTemporary: true,
                        };

                        this.notificationsService
                            .showNotification(noResultMessage)
                            .then(() => {
                                this.gridFacade.clearFilters();
                            });
                    }
                }),
            ),
        { dispatch: false },
    );

    public fetchGridDatasOnErrorOnFacetOnSSR$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(initGridPage, fetchGridDatasSuccess),
                filter(() => this.context.isInServerMode()),
                withLatestFrom(this.store.select(selectUpdateUrlFacet)),
                filter(([_action, facet]) => Boolean(facet.facet.missingFacet)),
                tap(([_action, facet]) => {
                    const builtMissingFacetUrl =
                        this.urlManagerService.buildUrl(facet);

                    this.logger.warn(
                        `Redirect to grid after wrong facet parameter passed : ${this.locale.urlPrefix}${builtMissingFacetUrl}`,
                    );

                    // Define maxAge to 9s because it not necessary to keep this cookie too long
                    this.response.cookie("missingFacet", "true", {
                        maxAge: 9000,
                    });

                    redirectResponse({
                        request: this.request,
                        response: this.response,
                        newPath: `${this.locale.urlPrefix}${builtMissingFacetUrl}`,
                        statusCode: 302,
                        forceCleanQueryParameters: true,
                    });
                }),
            ),
        { dispatch: false },
    );

    public backToGrid$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(scrollToPreviousProduct),
                filter(() => !this.context.isInServerMode()),
                withLatestFrom(
                    this.store.select(selectBackToGridProperties),
                    this.store.select(selectIsFromPreviousGrid),
                ),
                tap(([_action, backToGridProperties, isFromPreviousGrid]) => {
                    // Scroll to saved position if back to same grid
                    if (
                        isFromPreviousGrid &&
                        backToGridProperties.positionInPage !== 0
                    ) {
                        this.window.scroll(
                            0,
                            backToGridProperties.positionInPage,
                        );
                    }
                    // Get product grid selector if coming from product.
                    const gridPreviousProductSelector =
                        backToGridProperties.productSku
                            ? `#${gridItemIdFromSku(
                                  backToGridProperties.productSku,
                              )}`
                            : undefined;
                    // Focus grid item if valid selector.
                    if (gridPreviousProductSelector !== undefined) {
                        const gridElement = this.renderer.selectRootElement(
                            gridPreviousProductSelector,
                            true,
                        ) as HTMLElement;
                        if (gridElement) {
                            initFocusOnElement(gridElement);
                        }
                    }
                    // Reinit of position.
                    this.gridFacade.setIsFromPreviousGrid(false);
                    this.gridFacade.setBackToGridProperties(0);
                }),
            ),
        { dispatch: false },
    );

    public changePreviousPageOnNavigation$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(selectGridItem),
                tap((_action) => {
                    this.gridFacade.changePreviousPage(PRODUCT_PAGE);
                }),
            ),
        { dispatch: false },
    );

    public loadMoreProducts$ = createEffect(() => {
        const ofTypeLoadMoreProducts = ofType(loadMoreProducts);
        return this.gridEffect(
            this.actions$,
            this.store,
            this.featureFlagFacade,
            ofTypeLoadMoreProducts,
            false,
        );
    });

    public focusNextProductEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(loadMoreProductsSuccess),
                tap((action) => {
                    if (action.products?.length) {
                        const idToFocus = skuToGridProductId(
                            action.products[0].sku,
                        );
                        this.focusService.elementToFocus = idToFocus;
                        this.focusService.focusElementOnLoaderClose(false);
                    }
                }),
            ),
        { dispatch: false },
    );

    public fetchGridDatasFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fetchGridDatasFailure),
                map(({ error }) => {
                    this.logger.error(
                        `An '${error.name}' error occured when fetching grid datas: ${error.message}`,
                    );
                    throw error;
                }),
            ),
        { dispatch: false },
    );

    /**
     * scroll to top on grid page init or when apply a filter or apply sort or clear filters
     */
    public scrollTopEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    initGridPage,
                    updateGridFilter,
                    updateGridFilterPrice,
                    updateGridSort,
                    clearFilters,
                    clearPriceFilter,
                    closeDialog,
                ),
                withLatestFrom(this.store.select(selectGridState)),
                filter(() => this.context.isInBrowserMode()),
                // do not scroll when mobile dialog is open
                filter(([_action, state]) => !state.dialogOpen),
                tap(([action, _state]) => {
                    // on close dialog scroll only when user selected a filter
                    if (
                        (action.type === closeDialog.type &&
                            action.gridUpdated) ||
                        action.type !== closeDialog.type
                    ) {
                        this.window.scroll(0, 0);
                    }
                }),
            ),
        { dispatch: false },
    );

    public updateUrl$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateUrl, fetchGridDatasSuccess),
                switchMap(() =>
                    this.store.select(selectUpdateUrlFacet).pipe(
                        take(1),
                        tap((updateUrlFacet) => {
                            this.urlManagerService.updateUrl(updateUrlFacet);
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    public changeDisplayMode$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(changeDisplayMode),
                tap(({ displayMode }) => {
                    // save value into session storage
                    this.storageService.setSessionStorageItem(
                        "gridDisplayMode",
                        displayMode,
                    );
                }),
            ),
        { dispatch: false },
    );

    public fetchCategoryMetas$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchCategoryMetas),
            switchMap(({ categoryPimCode }) =>
                this.categoryService
                    .getCategoryFromDacFlat(categoryPimCode)
                    .pipe(
                        map((response) =>
                            fetchCategoryMetasSuccess({
                                categoryMetas: response,
                            }),
                        ),
                        catchError((error) =>
                            of(
                                fetchCategoryMetasFailure({
                                    error,
                                    categoryPimCode,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    public fetchGridCategoryMetasFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fetchCategoryMetasFailure),
                tap((action) => {
                    this.logger.info(
                        "Error fetch grid category metas",
                        action.categoryPimCode,
                        action.error,
                    );
                }),
            ),
        { dispatch: false },
    );

    /**
     * The two following effects updateSearchHeadTagAndSendEvent$, updateCategoryOrFacetMetasAndSendEvent$,
     * cannot have their analytic event separated. We must update metadata of the page
     * before sending event with correct information
     *
     * Furthermore, to gather all infomations, each case (search page, category page / facet page) must listen to different
     * action
     *
     * updateSearchHeadTagAndSendEvent$ must watch initGridPage to get if it is a search page info
     * (too quick to get an updated gridType from state)
     */
    public updateSearchHeadTagAndSendEvent$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(initGridPage),
                filter((action) => action.gridType === GridType.search),
                tap((action) => {
                    this.gridpageHeadService.updateHeadTagSearchPage(
                        action.searchTerm,
                    );
                    this.gridAnalyticsService.sendGridPageViewEvent(
                        getPageViewType(false)[action.gridType],
                    );
                }),
            ),
        { dispatch: false },
    );

    // First grid page load or grid to facet or facet to grid or grid to another grid cases.
    public sendPageViewEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchGridDatasSuccess),
            map((_action) =>
                sendPageViewEvent({ previousPageHasChanged: false }),
            ),
        ),
    );

    // Case product page to grid -> we send previousPageHasChanged to force through distinctUntilChanged() to update metadata;
    public sendPageViewEventBackOnGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initGridPage),
            withLatestFrom(this.store.select(selectPreviousPage)),
            filter(([_action, previousPage]) => previousPage !== GRID_PAGE),
            map(() =>
                sendPageViewEvent({
                    previousPageHasChanged: true,
                }),
            ),
        ),
    );

    // updateCategoryOrFacetMetasAndSendEvent$ watch fetchCategoryMetasSuccess to update metadata only on page change (not a filter change)
    public updateCategoryOrFacetMetas$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(sendPageViewEvent),
                withLatestFrom(
                    this.featureFlagFacade.isActivated(
                        SEO_COMBINED_FACETS_ACTIVATION_ON_GRID,
                    ),
                    this.store.select(selectGridState),
                ),
                filter(
                    ([_action, _isCombinedFacetActivated, state]) =>
                        state.gridType !== GridType.search,
                ),

                map(([_action, isCombinedFacetActivated, state]) => {
                    if (state.category && state.categoryMetas) {
                        this.gridpageHeadService.updateHeadTagCategoryPage({
                            category: state.category,
                            categoryMetas: state.categoryMetas,
                            combinedFacetActivated: isCombinedFacetActivated,
                            facet: state.facet,
                            seoPageParam: state.page.pageNumber,
                        });
                    }
                }),
            ),
        { dispatch: false },
    );

    public updateSeoPaginationHeadLinks$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateSeoPaginationHeadLinks),
                tap((_action) => {
                    this.gridpageHeadService.removeSeoPaginationHeadLinks();
                }),
                filter((action) => action.isSeoPaginationEnabled),
                tap((action) => {
                    let previousPageNumber;
                    let nextPageNumber;
                    const { viewSize } = getPageSizeValue();

                    if (action.pageNumber > 1) {
                        previousPageNumber = action.pageNumber - 1;
                    }

                    if (
                        action.pageNumber * viewSize <
                        action.totalProductsCount
                    ) {
                        nextPageNumber = action.pageNumber + 1;
                    }

                    this.gridpageHeadService.addSeoPaginationHeadLinks(
                        previousPageNumber,
                        nextPageNumber,
                    );
                }),
            ),
        { dispatch: false },
    );

    public navigateToProductPage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(selectGridItem),
                tap((action) => {
                    // build URL with SKU to focus product when back on grid
                    this.urlManagerService.savePositionSku(action.product.sku);

                    // navigate to product page
                    const isLeatherPersoProduct =
                        PRODUCT_LEATHER_CATEGORY.includes(
                            action.product.persoProductType,
                        );

                    const productUrl = generateLocalizedHref(
                        action.product.url,
                        this.locale,
                    );

                    if (isLeatherPersoProduct) {
                        this.routerService.navigate(
                            productUrl,
                            ANGULAR_SPA_ACTIVATION_ON_PERSO_GENERIC_CONFIGURATOR,
                        );
                    } else {
                        this.routerService.spaRedirect(productUrl);
                    }
                }),
            ),
        { dispatch: false },
    );

    public navigateToGridPage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(navigateToGridPage),
                tap(() => {
                    const url = this.urlManagerService.getUrlWithoutParams(
                        this.locale.urlPrefix,
                    );
                    // if browserMode -> angular redirect to first page, 301 redirect otherwise.
                    if (this.context.isInServerMode()) {
                        redirectResponse({
                            request: this.request,
                            response: this.response,
                            newPath: url,
                            forceCleanQueryParameters: true,
                        });
                    } else {
                        this.routerService.spaRedirect(url);
                    }
                }),
            ),
        { dispatch: false },
    );

    private renderer: Renderer2;

    constructor(
        @Inject(LOGGER) private logger: Logger,
        @Inject(REQUEST) private request: express.Request,
        @Inject(RESPONSE) private response: express.Response,
        @Inject(WINDOW) private window: Window,
        @Inject(LOCALE) private locale: Locale,
        private store: Store<fromGrid.GridState>,
        private actions$: Actions,
        private context: Context,
        private merchandisingService: MerchandisingService,
        private urlUtils: UrlUtils,
        private notificationsService: NotificationsService,
        private urlManagerService: UrlManagerService,
        private gridFacade: GridFacade,
        private storageService: StorageService,
        private categoryService: CategoryService,
        private gridpageHeadService: GridPageHeadService,
        private gridAnalyticsService: GridAnalyticsService,
        private focusService: FocusService,
        private routerService: RouterService,
        private featureFlagFacade: FeatureFlagFacade,
        private rendererFactory: RendererFactory2,
    ) {
        // eslint-disable-next-line unicorn/no-null
        this.renderer = this.rendererFactory.createRenderer(undefined, null);
    }

    private gridEffect(
        actions$: Actions,
        store: Store<fromGrid.GridState>,
        featureFlagFacade: FeatureFlagFacade,
        currentOfType: OperatorFunction<Action, unknown>,
        isFetchGridDatas: boolean,
    ) {
        return actions$.pipe(
            currentOfType,
            withLatestFrom(
                store.select(selectCriterias),
                featureFlagFacade.isActivated(
                    SEO_COMBINED_FACETS_ACTIVATION_ON_GRID,
                ),
                store.select(selectFacet),
            ),
            switchMap(
                ([_action, criterias, isCombinedFacetActivated, facet]) => {
                    const { viewSize } = getPageSizeValue();
                    // compute pagination for load more products
                    const offset = isFetchGridDatas
                        ? 0
                        : criterias.pagesLoaded * viewSize;
                    const pageSize = viewSize;
                    return this.merchandisingService
                        .fetchGridDataFromCriterias(
                            { ...criterias, pageSize, offset },
                            isCombinedFacetActivated,
                            facet,
                        )
                        .pipe(
                            map((gridData: GridData) => {
                                if (isFetchGridDatas) {
                                    // save grid datas
                                    return fetchGridDatasSuccess({
                                        ...retrieveGridDataWithoutNoDisplayedVideoAssets(
                                            gridData,
                                        ),
                                        isFirstPage: true,
                                        isFromPreviousGrid: true,
                                    });
                                }
                                // save new products
                                return loadMoreProductsSuccess({
                                    total: gridData.total,
                                    products:
                                        retrieveGridDataWithoutNoDisplayedVideoAssets(
                                            gridData,
                                        ).products,
                                });
                            }),
                            catchError((error) =>
                                of(fetchGridDatasFailure({ error })),
                            ),
                        );
                },
            ),
        );
    }
}
